// src/services/sessionService.ts
import { logger } from '../utils/logger';

// ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ UPDATED: Added is_admin to ALL profile queries

import { supabase } from './supabase';
import { moderateChatMessage } from './contentModeration';

import {

  Session,

  SessionWithDetails,

  SessionParticipant,

  SessionGame,

  SessionMessage,

  SessionSupply,

  SessionSupplyWithUser,

  LocationType,

  RSVPStatus,

} from '../types/session';

class SessionService {

  // Create a new session

  async createSession(sessionData: {

    title: string;

    description?: string;

    session_date: string;

    start_time: string;

    end_time?: string;

    location_type: LocationType;

    location_details?: string;

    platform?: string;

    max_players?: number;

    group_id?: string;

    invited_user_ids?: string[];

  }): Promise<{ success: boolean; session?: SessionWithDetails; error?: string }> {

    try {

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {

        return { success: false, error: 'Not authenticated' };

      }

      // Content moderation for session title and description
      const titleCheck = moderateChatMessage(sessionData.title);
      if (!titleCheck.isClean) {
        return {
          success: false,
          error: 'Session title contains inappropriate content. This platform does not tolerate hate speech or slurs.'
        };
      }

      if (sessionData.description) {
        const descCheck = moderateChatMessage(sessionData.description);
        if (!descCheck.isClean) {
          return {
            success: false,
            error: 'Session description contains inappropriate content. This platform does not tolerate hate speech or slurs.'
          };
        }
      }

      let groupId = sessionData.group_id;

      // If no group_id provided, create a new group for this session

      if (!groupId) {

        const { data: newGroup, error: groupError } = await supabase

          .from('groups')

          .insert({

            name: `${sessionData.title} - Session Chat`,

            creator_id: user.id,

          })

          .select()

          .single();

        if (groupError || !newGroup) {

          logger.error('Error creating group:', groupError);

          return { success: false, error: 'Failed to create session group' };

        }

        groupId = newGroup.id;

        // Add host to the group as admin

        await supabase.from('group_members').insert({

          group_id: groupId,

          user_id: user.id,

          role: 'admin',

        });

      }

      // Create the session

      const { data: session, error: sessionError } = await supabase

        .from('sessions')

        .insert({

          host_id: user.id,

          group_id: groupId,

          title: sessionData.title,

          description: sessionData.description,

          session_date: sessionData.session_date,

          start_time: sessionData.start_time,

          end_time: sessionData.end_time,

          location_type: sessionData.location_type,

          location_details: sessionData.location_details,

          platform: sessionData.platform,

          max_players: sessionData.max_players,

          status: 'planning',

        })

        .select()

        .single();

      if (sessionError || !session) {

        logger.error('Error creating session:', sessionError);

        return { success: false, error: sessionError?.message };

      }

      // Link the group to this session

      await supabase

        .from('groups')

        .update({ session_id: session.id })

        .eq('id', groupId);

      // Add host as participant with accepted status

      await supabase.from('session_participants').insert({

        session_id: session.id,

        user_id: user.id,

        invited_by: user.id,

        rsvp_status: 'accepted',

        can_invite: true,

      });

      // Invite initial users if provided (they'll be pending, NOT in group yet)

      if (sessionData.invited_user_ids && sessionData.invited_user_ids.length > 0) {

        const invites = sessionData.invited_user_ids.map(userId => ({

          session_id: session.id,

          user_id: userId,

          invited_by: user.id,

          rsvp_status: 'pending' as RSVPStatus,

          can_invite: true,

        }));

        await supabase.from('session_participants').insert(invites);

        // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Â FIX #4: Send push notifications for session invites

        logger.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬Å“Ã‚Â¤ Sending session invite notifications to:', sessionData.invited_user_ids);

        // Get host profile for notification

        const { data: hostProfile } = await supabase

          .from('profiles')

          .select('username, display_name')

          .eq('id', user.id)

          .single();

        const hostName = hostProfile?.display_name || hostProfile?.username || 'Someone';

        // Send notification to each invited user

        for (const invitedUserId of sessionData.invited_user_ids) {

          supabase.functions.invoke('send-notification', {

            body: {

              type: 'session_invite',

              recipientId: invitedUserId,

              senderId: user.id,

              senderName: hostName,

              sessionId: session.id,

              sessionTitle: sessionData.title,

              sessionDate: sessionData.session_date,

              sessionTime: sessionData.start_time,

            }

          }).then(result => {

            if (result.error) {

              logger.error('Push notification error for session invite:', result.error);

            } else {

              logger.log('ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Session invite notification sent to:', invitedUserId);

            }

          }).catch(err => {

            logger.error('Exception sending session invite notification:', err);

          });

        }

      }

      // Fetch the complete session data

      const fullSession = await this.getSessionById(session.id);

      return { success: true, session: fullSession || undefined };

    } catch (error) {

      logger.error('Error in createSession:', error);

      return { success: false, error: 'Failed to create session' };

    }

  }

  // Get session by ID with all details

  async getSessionById(sessionId: string): Promise<SessionWithDetails | null> {

    try {

      // FIXED: Added is_admin to host and user profiles

      const { data, error } = await supabase

        .from('sessions')

        .select(`

          *,

          host:profiles!sessions_host_id_fkey(id, username, display_name, meeple_avatar, is_admin),

          participants:session_participants(

            *,

            user:profiles!session_participants_user_id_fkey(id, username, display_name, meeple_avatar, is_admin),

            invited_by_user:profiles!session_participants_invited_by_fkey(username, display_name)

          ),

          games:session_games(

            *,

            game:games(id, name, bgg_id),

            suggested_by_user:profiles!session_games_suggested_by_fkey(id, username, display_name)

          )

        `)

        .eq('id', sessionId)

        .single();

      if (error) {

        logger.error('Error fetching session:', error);

        return null;

      }

      // Map meeple_avatar to avatar for host and participants
      if (data.host) {
        data.host.avatar = data.host.meeple_avatar;
      }
      if (data.participants) {
        data.participants.forEach((p: any) => {
          if (p.user) {
            p.user.avatar = p.user.meeple_avatar;
          }
        });
      }

      return data as SessionWithDetails;

    } catch (error) {

      logger.error('Error in getSessionById:', error);

      return null;

    }

  }

  // Get all sessions for current user - OPTIMIZED VERSION
  async getMySessions(): Promise<SessionWithDetails[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // OPTIMIZED: Single query using session_participants join
      // This gets ALL sessions where user is participant (including as host) in one go
      const { data: participantSessions, error } = await supabase
        .from('session_participants')
        .select(`
          session:sessions(
            *,
            host:profiles!sessions_host_id_fkey(id, username, display_name, meeple_avatar, is_admin),
            participants:session_participants(
              *,
              user:profiles!session_participants_user_id_fkey(id, username, display_name, meeple_avatar, is_admin)
            )
          )
        `)
        .eq('user_id', user.id);

      if (error) {
        logger.error('Error fetching sessions:', error);
        return [];
      }

      // Extract sessions and remove nulls
      // Type assertion needed because Supabase doesn't infer nested query types well
      const sessions = ((participantSessions || []) as any[])
        .map((p: any) => p.session)
        .filter((s: any): s is SessionWithDetails => s !== null);

      // Remove duplicates by ID (shouldn't happen but just in case)
      const uniqueSessions = Array.from(
        new Map(sessions.map((s: SessionWithDetails) => [s.id, s])).values()
      );

      // Sort by date and time (upcoming first)
      uniqueSessions.sort((a, b) => {
        const dateCompare = a.session_date.localeCompare(b.session_date);
        if (dateCompare !== 0) return dateCompare;
        return a.start_time.localeCompare(b.start_time);
      });

      // Map meeple_avatar to avatar for host and participants
      uniqueSessions.forEach((session: any) => {
        if (session.host) {
          session.host.avatar = session.host.meeple_avatar;
        }
        if (session.participants) {
          session.participants.forEach((p: any) => {
            if (p.user) {
              p.user.avatar = p.user.meeple_avatar;
            }
          });
        }
      });

      return uniqueSessions;
    } catch (error) {
      logger.error('Error in getMySessions:', error);
      return [];
    }
  }

  // Update session (host only)

  async updateSession(

    sessionId: string,

    updates: Partial<Session>

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { error } = await supabase

        .from('sessions')

        .update(updates)

        .eq('id', sessionId);

      if (error) {

        logger.error('Error updating session:', error);

        return { success: false, error: error.message };

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in updateSession:', error);

      return { success: false, error: 'Failed to update session' };

    }

  }

  // Delete session (host only)

  async deleteSession(sessionId: string): Promise<{ success: boolean; error?: string }> {

    try {

      const { error } = await supabase

        .from('sessions')

        .delete()

        .eq('id', sessionId);

      if (error) {

        logger.error('Error deleting session:', error);

        return { success: false, error: error.message };

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in deleteSession:', error);

      return { success: false, error: 'Failed to delete session' };

    }

  }

  // Invite users to session (pending status, NOT added to group chat yet)

  async inviteUsers(

    sessionId: string,

    userIds: string[]

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {

        return { success: false, error: 'Not authenticated' };

      }

      // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ FIX #2: Check for existing participants first to avoid duplicates

      const { data: existingParticipants } = await supabase

        .from('session_participants')

        .select('user_id')

        .eq('session_id', sessionId)

        .in('user_id', userIds);

      const existingUserIds = new Set(existingParticipants?.map(p => p.user_id) || []);

      const newUserIds = userIds.filter(id => !existingUserIds.has(id));

      if (newUserIds.length === 0) {

        return { success: true }; // All users already invited

      }

      const invites = newUserIds.map(userId => ({

        session_id: sessionId,

        user_id: userId,

        invited_by: user.id,

        rsvp_status: 'pending' as RSVPStatus,

        can_invite: true,

      }));

      // Add participants but DON'T add to group chat yet

      // They'll be added when they accept the invitation

      const { error } = await supabase

        .from('session_participants')

        .insert(invites);

      if (error) {

        logger.error('Error inviting users:', error);

        return { success: false, error: error.message };

      }

      // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Â FIX #4: Send push notifications for new invites

      const { data: session } = await supabase

        .from('sessions')

        .select('title, session_date, start_time')

        .eq('id', sessionId)

        .single();

      const { data: inviterProfile } = await supabase

        .from('profiles')

        .select('username, display_name')

        .eq('id', user.id)

        .single();

      const inviterName = inviterProfile?.display_name || inviterProfile?.username || 'Someone';

      for (const userId of newUserIds) {

        supabase.functions.invoke('send-notification', {

          body: {

            type: 'session_invite',

            recipientId: userId,

            senderId: user.id,

            senderName: inviterName,

            sessionId: sessionId,

            sessionTitle: session?.title || 'a gaming session',

            sessionDate: session?.session_date,

            sessionTime: session?.start_time,

          }

        }).then(result => {

          if (result.error) {

            logger.error('Push notification error:', result.error);

          }

        }).catch(err => {

          logger.error('Exception sending notification:', err);

        });

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in inviteUsers:', error);

      return { success: false, error: 'Failed to invite users' };

    }

  }

  /**
   * Invite participants to a session (alias for inviteUsers for consistency)
   */
  async inviteParticipants(
    sessionId: string,
    userIds: string[]
  ): Promise<{ success: boolean; error?: string }> {
    // Just call inviteUsers - same functionality
    return this.inviteUsers(sessionId, userIds);
  }

  // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ FIX #1: Update RSVP status and manage group chat membership

  async updateRSVP(

    sessionId: string,

    status: RSVPStatus

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {

        return { success: false, error: 'Not authenticated' };

      }

      logger.log(`ÃƒÂ°Ã…Â¸Ã…Â½Ã‚Â¯ Updating RSVP: sessionId=${sessionId}, userId=${user.id}, status=${status}`);

      // Check if user is already a participant

      const { data: existingParticipant } = await supabase

        .from('session_participants')

        .select('*')

        .eq('session_id', sessionId)

        .eq('user_id', user.id)

        .maybeSingle();

      if (!existingParticipant) {

        logger.error('ÃƒÂ¢Ã‚ÂÃ…â€™ User is not a participant of this session');

        return { success: false, error: 'You are not invited to this session' };

      }

      // Update RSVP status

      const { error: updateError } = await supabase

        .from('session_participants')

        .update({ rsvp_status: status })

        .eq('session_id', sessionId)

        .eq('user_id', user.id);

      if (updateError) {

        logger.error('Error updating RSVP:', updateError);

        return { success: false, error: updateError.message };

      }

      logger.log('ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ RSVP status updated successfully');

      // Get the session's group_id

      const { data: session, error: sessionError } = await supabase

        .from('sessions')

        .select('group_id, host_id, title')

        .eq('id', sessionId)

        .single();

      if (sessionError || !session?.group_id) {

        logger.error('Error fetching session:', sessionError);

        // Don't fail if we can't update group membership

        return { success: true };

      }

      const groupId = session.group_id;

      // If user accepted, add them to the group chat

      if (status === 'accepted') {

        // Check if they're already in the group

        const { data: existingMember } = await supabase

          .from('group_members')

          .select('id')

          .eq('group_id', groupId)

          .eq('user_id', user.id)

          .maybeSingle();

        // Add to group if not already a member

        if (!existingMember) {

          const { error: groupError } = await supabase

            .from('group_members')

            .insert({

              group_id: groupId,

              user_id: user.id,

              role: 'member',

            });

          if (groupError) {

            logger.error('Error adding to group:', groupError);

            // Don't fail the RSVP update if group add fails

          } else {

            logger.log(`ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ User ${user.id} added to group ${groupId} after accepting`);

          }

        }

        // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Â Notify host that someone accepted

        const { data: userProfile } = await supabase

          .from('profiles')

          .select('username, display_name')

          .eq('id', user.id)

          .single();

        const userName = userProfile?.display_name || userProfile?.username || 'Someone';

        supabase.functions.invoke('send-notification', {

          body: {

            type: 'session_rsvp_accepted',

            recipientId: session.host_id,

            senderId: user.id,

            senderName: userName,

            sessionId: sessionId,

            sessionTitle: session.title,

          }

        }).catch(err => logger.error('Error sending RSVP notification:', err));

      }

      // If user declined, remove them from the group chat

      if (status === 'declined') {

        const { error: removeError } = await supabase

          .from('group_members')

          .delete()

          .eq('group_id', groupId)

          .eq('user_id', user.id);

        if (removeError) {

          logger.error('Error removing from group:', removeError);

        } else {

          logger.log(`ÃƒÂ°Ã…Â¸Ã¢â‚¬â€Ã¢â‚¬ËœÃƒÂ¯Ã‚Â¸Ã‚Â User ${user.id} removed from group ${groupId} after declining`);

        }

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in updateRSVP:', error);

      return { success: false, error: 'Failed to update RSVP' };

    }

  }

  // Remove participant (host can kick, users can leave)

  async removeParticipant(

    sessionId: string,

    userId: string

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { error } = await supabase

        .from('session_participants')

        .delete()

        .eq('session_id', sessionId)

        .eq('user_id', userId);

      if (error) {

        logger.error('Error removing participant:', error);

        return { success: false, error: error.message };

      }

      // Also remove from group chat

      const { data: session } = await supabase

        .from('sessions')

        .select('group_id')

        .eq('id', sessionId)

        .single();

      if (session?.group_id) {

        await supabase

          .from('group_members')

          .delete()

          .eq('group_id', session.group_id)

          .eq('user_id', userId);

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in removeParticipant:', error);

      return { success: false, error: 'Failed to remove participant' };

    }

  }

  // Send message to session group chat

  async sendMessage(

    sessionId: string,

    message: string

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {

        return { success: false, error: 'Not authenticated' };

      }

      // Content moderation - block hate speech and slurs
      const moderationCheck = moderateChatMessage(message);
      if (!moderationCheck.isClean) {
        return {
          success: false,
          error: 'Message contains inappropriate content. This platform does not tolerate hate speech or slurs.'
        };
      }

      // Get the group_id for this session

      const { data: session } = await supabase

        .from('sessions')

        .select('group_id')

        .eq('id', sessionId)

        .single();

      if (!session || !session.group_id) {

        return { success: false, error: 'Session not found' };

      }

      // Send message to the group chat

      const { error } = await supabase

        .from('messages')

        .insert({

          group_id: session.group_id,

          sender_id: user.id,

          content: message.trim(),

        });

      if (error) {

        logger.error('Error sending message:', error);

        return { success: false, error: error.message };

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in sendMessage:', error);

      return { success: false, error: 'Failed to send message' };

    }

  }

  // Get session messages (from group chat)

  async getSessionMessages(sessionId: string): Promise<any[]> {

    try {

      // Get the group_id for this session

      const { data: session } = await supabase

        .from('sessions')

        .select('group_id')

        .eq('id', sessionId)

        .single();

      if (!session || !session.group_id) {

        return [];

      }

      // Get messages from the group chat - FIXED: Added is_admin to sender profile

      const { data, error } = await supabase

        .from('messages')

        .select(`

          *,

          sender:profiles!messages_sender_id_fkey(id, username, display_name, meeple_avatar, is_admin)

        `)

        .eq('group_id', session.group_id)

        .order('created_at', { ascending: true });

      if (error) {

        logger.error('Error fetching messages:', error);

        return [];

      }

      // Map meeple_avatar to avatar for message senders
      if (data) {
        data.forEach((message: any) => {
          if (message.sender) {
            message.sender.avatar = message.sender.meeple_avatar;
          }
        });
      }

      return data || [];

    } catch (error) {

      logger.error('Error in getSessionMessages:', error);

      return [];

    }

  }

  // Suggest a game for the session

  async suggestGame(

    sessionId: string,

    gameId: string

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {

        return { success: false, error: 'Not authenticated' };

      }

      const { error } = await supabase

        .from('session_games')

        .insert({

          session_id: sessionId,

          game_id: gameId,

          suggested_by: user.id,

          votes: 0,

        });

      if (error) {

        logger.error('Error suggesting game:', error);

        return { success: false, error: error.message };

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in suggestGame:', error);

      return { success: false, error: 'Failed to suggest game' };

    }

  }

  // Vote on a game

  async voteGame(

    sessionGameId: string,

    increment: boolean = true

  ): Promise<{ success: boolean; error?: string }> {

    try {

      const { data: game } = await supabase

        .from('session_games')

        .select('votes')

        .eq('id', sessionGameId)

        .single();

      if (!game) {

        return { success: false, error: 'Game not found' };

      }

      const newVotes = increment ? game.votes + 1 : Math.max(0, game.votes - 1);

      const { error } = await supabase

        .from('session_games')

        .update({ votes: newVotes })

        .eq('id', sessionGameId);

      if (error) {

        logger.error('Error voting on game:', error);

        return { success: false, error: error.message };

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in voteGame:', error);

      return { success: false, error: 'Failed to vote' };

    }

  }

  // Remove game suggestion

  async removeGame(sessionGameId: string): Promise<{ success: boolean; error?: string }> {

    try {

      const { error } = await supabase

        .from('session_games')

        .delete()

        .eq('id', sessionGameId);

      if (error) {

        logger.error('Error removing game:', error);

        return { success: false, error: error.message };

      }

      return { success: true };

    } catch (error) {

      logger.error('Error in removeGame:', error);

      return { success: false, error: 'Failed to remove game' };

    }

  }

  // ============================================================================
  // SESSION SUPPLIES METHODS ("Who's Bringing What?" Checklist)
  // ============================================================================

  // Get all supplies for a session
  async getSessionSupplies(sessionId: string): Promise<{ success: boolean; supplies?: SessionSupplyWithUser[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('session_supplies')
        .select(`
          *,
          claimed_by_user:claimed_by(id, username, display_name, avatar, is_admin),
          added_by_user:added_by(id, username, display_name, avatar, is_admin)
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) {
        logger.error('Error fetching session supplies:', error);
        return { success: false, error: error.message };
      }

      return { success: true, supplies: data || [] };
    } catch (error) {
      logger.error('Error in getSessionSupplies:', error);
      return { success: false, error: 'Failed to fetch supplies' };
    }
  }

  // Add a supply item to a session
  async addSupply(sessionId: string, itemName: string): Promise<{ success: boolean; supply?: SessionSupplyWithUser; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Content moderation
      const itemCheck = moderateChatMessage(itemName);
      if (!itemCheck.isClean) {
        return {
          success: false,
          error: 'Item name contains inappropriate content.'
        };
      }

      const { data, error } = await supabase
        .from('session_supplies')
        .insert({
          session_id: sessionId,
          item_name: itemName.trim(),
          added_by: user.id
        })
        .select(`
          *,
          claimed_by_user:claimed_by(id, username, display_name, avatar, is_admin),
          added_by_user:added_by(id, username, display_name, avatar, is_admin)
        `)
        .single();

      if (error) {
        logger.error('Error adding supply:', error);
        return { success: false, error: error.message };
      }

      return { success: true, supply: data };
    } catch (error) {
      logger.error('Error in addSupply:', error);
      return { success: false, error: 'Failed to add supply' };
    }
  }

  // Claim a supply item (or unclaim if already claimed by user)
  async claimSupply(supplyId: string, claim: boolean): Promise<{ success: boolean; supply?: SessionSupplyWithUser; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { data, error } = await supabase
        .from('session_supplies')
        .update({
          claimed_by: claim ? user.id : null
        })
        .eq('id', supplyId)
        .select(`
          *,
          claimed_by_user:claimed_by(id, username, display_name, avatar, is_admin),
          added_by_user:added_by(id, username, display_name, avatar, is_admin)
        `)
        .single();

      if (error) {
        logger.error('Error claiming supply:', error);
        return { success: false, error: error.message };
      }

      return { success: true, supply: data };
    } catch (error) {
      logger.error('Error in claimSupply:', error);
      return { success: false, error: 'Failed to claim supply' };
    }
  }

  // Delete a supply item
  async deleteSupply(supplyId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('session_supplies')
        .delete()
        .eq('id', supplyId);

      if (error) {
        logger.error('Error deleting supply:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      logger.error('Error in deleteSupply:', error);
      return { success: false, error: 'Failed to delete supply' };
    }
  }

}

export const sessionService = new SessionService();